/* eslint-disable react-refresh/only-export-components */
import { useMemo } from "react";
import Tag from "@/components/Tag";
import { OrderPaymentStatus, OrderStage, PaymentMethod, PaymentStatus } from "./types";
import { TagType } from "@/components/Tag/Tag";
import type { MaterialNames } from "@/components/material_symbol_names";
import { capitalize, sumBy, groupBy, orderBy } from "lodash";
import type { OrderFormValue } from "./orders/apply-payment";
import { PayerType } from "./orders/[orderId]/payment/[paymentId]/types";
import { toFormat, toUTCFormat } from "@/lib/dateHelpers";
import { Row, buildAvatarDetailsColumn } from "@/components/Table";
import Tooltip from "@/components/Tooltip";
import Checkbox from "@/components/Checkbox";
import { Table as TableComponent } from "@tanstack/react-table";
import { Person } from "@/types";
import ReactMarkdown from "react-markdown";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { StepType } from "@/components/Stepper/Stepper";
import { ChangesetItem, CommerceAuditLogEntry, GroupedAuditEvents } from "./types";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";

type StageTag = { color: TagType; label: string; icon?: MaterialNames; supportingText?: string };

const START_DATE = DateTime.fromISO("2025-11-20T00:00:00Z");

export const STAGES_MAP: Record<OrderStage, StageTag> = {
  [OrderStage.PAID]: {
    color: "success",
    label: "Paid",
    supportingText: "Order is completely paid"
  },
  [OrderStage.OPEN]: {
    color: "warn",
    label: "Open",
    supportingText: "Order is available to payor"
  },
  [OrderStage.DRAFT]: {
    color: "information",
    label: "Draft",
    supportingText: "Order is internal only"
  },
  [OrderStage.CANCELED]: { color: "surface", label: "Canceled", supportingText: "Order is not payable" },
  [OrderStage.UNCOLLECTIBLE]: { color: "surface", label: "Uncollectible", supportingText: "Order is not payable" },
  [OrderStage.OVERDUE]: { color: "error", label: "Overdue", supportingText: "Order is overdue" },
  [OrderStage.PENDING]: {
    color: "warn",
    label: "Pending",
    supportingText: "Order has a payment being processed"
  },
  [OrderStage.FAILED]: {
    color: "error",
    label: "Failed",
    icon: "error"
  }
};

type OrderPaymentStatusTag = { icon?: MaterialNames; supportingText?: string };

const ORDER_PAYMENT_STATUS_MAP: Record<OrderPaymentStatus, OrderPaymentStatusTag> = {
  [OrderPaymentStatus.FAILED]: { icon: "error", supportingText: "Order has a failed payment" },
  [OrderPaymentStatus.OVERDUE]: { icon: "schedule", supportingText: "Order is overdue" },
  [OrderPaymentStatus.REFUNDED]: { icon: "money_off", supportingText: "Order is not payable and has refunded payments" }
};

export const renderStatus = (
  stage: OrderStage,
  paymentStatus: OrderPaymentStatus,
  tagStyle: "standard" | "strong" = "strong"
) => {
  const data = STAGES_MAP[stage] || { label: capitalize(stage), color: "tertiary" as TagType };
  const icon = ORDER_PAYMENT_STATUS_MAP[paymentStatus]?.icon || data?.icon;
  const supportingText = ORDER_PAYMENT_STATUS_MAP[paymentStatus]?.supportingText || data?.supportingText;
  return (
    <Tooltip
      supportingText={supportingText || ""}
      variant="plain"
      color="black"
      menuCorner="end-start"
      anchorCorner="start-start"
      positioning="fixed"
      anchorClassName="w-fit"
    >
      <Tag tagType={data?.color as TagType} tagStyle={tagStyle} testId={data.label} iconName={icon}>
        {data.label}
      </Tag>
    </Tooltip>
  );
};

const PAYMENT_TYPE_MAP = {
  [PaymentMethod.US_BANK_ACCOUNT]: "ACH",
  [PaymentMethod.CARD]: "Credit Card",
  [PaymentMethod.CHECK]: "Check",
  [PaymentMethod.FINANCIAL_ADJUSTMENT]: "Finance Adjustment"
};

export const renderPaymentType = (paymentType: PaymentMethod) => {
  return PAYMENT_TYPE_MAP[paymentType];
};

const PAYMENT_STATUS_MAP: Record<PaymentStatus, StageTag> = {
  [PaymentStatus.FAILED]: { color: "error", label: "Failed", supportingText: "Payment failed" },
  [PaymentStatus.PAID]: {
    color: "success",
    label: "Paid",
    supportingText: "Payment successful"
  },
  [PaymentStatus.PARTIALLY_REFUNDED]: {
    color: "success",
    label: "Paid",
    icon: "money_off",
    supportingText: "Payment has been partially refunded"
  },
  [PaymentStatus.PENDING]: {
    color: "warn",
    label: "Pending",
    supportingText: "Payment being processed"
  },
  [PaymentStatus.REFUNDED]: {
    color: "surface",
    label: "Refunded",
    icon: "money_off",
    supportingText: "Payment has been fully refunded"
  }
};

export const renderPaymentStatus = (status: PaymentStatus, tagStyle: "standard" | "strong" = "strong") => {
  const data = PAYMENT_STATUS_MAP[status] || { label: capitalize(status), color: "tertiary" as TagType };

  return (
    <Tooltip supportingText={data?.supportingText || ""} variant="plain" color="black">
      <Tag tagType={data?.color as TagType} tagStyle={tagStyle} testId={data.label} iconName={data?.icon}>
        {data.label}
      </Tag>
    </Tooltip>
  );
};

export function buildTableData(orders: OrderFormValue[], paymentAmount?: number | null) {
  const orderData = orders.map((order) => {
    const allocatedAddons = sumBy(
      order.addons.filter((l) => l.selected),
      (item) => item.allocated || 0
    );
    const allocatedLineItems = sumBy(
      order.lineItems.filter((l) => l.selected),
      (item) => item.allocated || 0
    );
    return {
      ...order,
      amount: order.balance + allocatedAddons,
      totalAllocated: allocatedAddons + allocatedLineItems,
      balanceAfterPayment: order.balance - allocatedLineItems
    };
  });

  const totalAmount = sumBy(orderData, (order) => order.amount);
  const totalAllocated = sumBy(orderData, (order) => order.totalAllocated);
  const totals = {
    totalAmount,
    totalAllocated,
    totalUnallocated: (paymentAmount || 0) - totalAllocated,
    totalRemainings: totalAmount - totalAllocated
  };

  return {
    orderData,
    totals
  };
}

type PayerOptionType = { label: string; icon?: MaterialNames };

const PAYER_TYPE_MAP: Record<PayerType, PayerOptionType> = {
  [PayerType.FACILITY]: { icon: "location_on", label: "Facility" },
  [PayerType.SELF]: { icon: "person", label: "Name" },
  [PayerType.OTHER]: { icon: "person", label: "Proxy Check" }
};

export const getPayerOption = (type: PayerType) => {
  const data = PAYER_TYPE_MAP[type] || { icon: "person", label: capitalize(type) };

  return data;
};

export const paymentDate = (paymentMethod: PaymentMethod, date?: string) => {
  if (!date) return "";

  if (paymentMethod === PaymentMethod.CHECK || paymentMethod === PaymentMethod.FINANCIAL_ADJUSTMENT) {
    return toUTCFormat(date, "LLL dd, yyyy");
  } else {
    return toFormat(date, "LLL dd, yyyy t");
  }
};

const regex = /([A-Z])-(\d{1,2})/;

export const getClassification = (str: string) => {
  const match = str.match(regex);
  if (match) {
    const matchIndex = str.indexOf(match[0]);
    return {
      name: str.substring(0, matchIndex),
      classificationLetter: match[1],
      classificationNumber: Number(match[2])
    };
  }
  return {
    name: str,
    classificationLetter: null,
    classificationNumber: null
  };
};

export const sourcesData = Object.entries(PAYMENT_TYPE_MAP).map(([key, value]) => ({
  label: value,
  value: key
}));

export const paymentStatusData = Object.entries(PAYMENT_STATUS_MAP).map(([key, value]) => ({
  label: key === PaymentStatus.PARTIALLY_REFUNDED ? "Partially Refunded" : value.label,
  value: key
}));

type buildColumnsProps<
  T extends {
    person: Person;
  }
> = {
  isCompactLayout: boolean;
  hasCheckbox?: boolean;
  selectableRows?: T[];
  getIsSomeRowsSelected?: () => boolean;
  setIsSelectedOnly?: (isSelectedOnly: boolean) => void;
};

export function buildColumns<
  T extends {
    person: Person;
  }
>({
  isCompactLayout,
  hasCheckbox = true,
  selectableRows,
  getIsSomeRowsSelected,
  setIsSelectedOnly
}: buildColumnsProps<T>) {
  return [
    ...(hasCheckbox
      ? [
          {
            id: "select",
            header: ({ table }: { table: TableComponent<T> }) => (
              <Checkbox
                name="select"
                checked={table.getIsAllRowsSelected() && (selectableRows?.length ?? 0) > 0}
                indeterminate={getIsSomeRowsSelected?.() ?? false}
                onChange={(checked) => {
                  table.getToggleAllRowsSelectedHandler()({
                    target: { checked: (getIsSomeRowsSelected?.() ?? false) ? true : checked }
                  });
                  setIsSelectedOnly?.(false);
                }}
              />
            ),
            cell: ({ row }: { row: Row<T> }) => (
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  name={`select-row-${row.id}`}
                  disabled={!row.getCanSelect()}
                  onChange={(checked) => {
                    row.getToggleSelectedHandler()({ target: { checked } });
                  }}
                  checked={row.getIsSelected()}
                />
              </div>
            ),
            size: 40,
            meta: {
              className: "pr-0"
            }
          }
        ]
      : []),
    ...buildAvatarDetailsColumn<T>({
      isCompactLayout,
      getTitle: (row) => `${row.person.firstName} ${row.person.lastName}`,
      getSubtitle: (row) => row.person.mainProgramType?.name || "",
      getPicture: (row) => row.person.profilePhoto,
      header: "Name",
      id: "fullName",
      size: 240,
      minSize: 200
    })
  ];
}

const getActorName = (events: CommerceAuditLogEntry[]) => {
  const actor = events[0]?.actor;

  if (actor?.__typename === "BackofficePerson" && actor?.displayName === "PGA Admin") {
    return "System";
  }
  if (!actor) return "";

  switch (actor.__typename) {
    case "BackofficeUser":
      return actor.name || actor.email || "";
    case "BackofficeAdminUser":
      return actor.email || "";
    case "BackofficePerson":
      return actor.displayName || "";
    default:
      return "";
  }
};

const markdownComponents = {
  p: ({ node, ...props }: { node?: unknown; [key: string]: any }) => <p {...props} />
};

const isLineItemEvent = (e: CommerceAuditLogEntry) => e.description?.toLowerCase().includes("line item");
const isCompletedPaymentEvent = (e: CommerceAuditLogEntry) =>
  !e.description.toLowerCase().includes("pending") && !e.description.toLowerCase().includes("failed");
const isPaymentEvent = (e: any) =>
  e.description?.toLowerCase().includes("payment") === true &&
  e.description?.toLowerCase().includes("refund") === false;

const getPriceCentsChangesets = (events: CommerceAuditLogEntry[]): ChangesetItem[] =>
  events
    .filter((e) => isCompletedPaymentEvent(e))
    .flatMap((e) =>
      (e.changeset ?? [])
        .filter((c) => c?.typeOfData?.includes("cents"))
        .map((c) => ({
          ...c,
          description: e.description
        }))
    );

const calculateBalanceDelta = (changesets: ChangesetItem[]): number => {
  return changesets.reduce((sum, changeset) => {
    const oldValue = Number(changeset.oldValue) || 0;
    const newValue = Number(changeset.newValue) || 0;
    const amountChange = newValue - oldValue;

    return sum + (isPaymentEvent(changeset) ? -amountChange : amountChange);
  }, 0);
};

export const formatMarkdownText = (event: CommerceAuditLogEntry) => {
  return event.description;
};

export function groupAuditEventsByTimestamp(
  auditEvents: CommerceAuditLogEntry[]
): Omit<GroupedAuditEvents, "balanceChange">[] {
  const grouped = groupBy(auditEvents, "timestamp");

  const groups = Object.entries(grouped).map(([timestamp, events]) => {
    const sortedEvents = orderBy(
      events,
      [(e) => (isLineItemEvent(e) ? 0 : 1), (e) => Number(e.id ?? 0)],
      ["asc", "asc"]
    );

    return { timestamp, events: sortedEvents };
  });

  return orderBy(groups, [(g) => new Date(g.timestamp).getTime()], ["desc"]);
}

const getOrderCreatedBalance = (events: CommerceAuditLogEntry[]): number => {
  return events.reduce((total, event) => {
    const priceCents = (event.changeset ?? [])
      .filter((c) => c?.typeOfData === "Price cents")
      .reduce((sum, c) => sum + (Number(c.newValue) || 0), 0);
    return total + priceCents;
  }, 0);
};

const formatEventDescription = (event: CommerceAuditLogEntry, events: CommerceAuditLogEntry[]): string => {
  if (event.description === "Order created") {
    const balance = getOrderCreatedBalance(events);
    return `Order created with balance ${centsToDollarsFormatted(balance)}`;
  }
  return event.description;
};

/* eslint-disable react/prop-types */
export const Headline: React.FC<{
  events: CommerceAuditLogEntry[];
}> = ({ events }) => {
  if (!events.length) return null;

  return (
    <div className="">
      {events.map((event, i) => (
        <div key={`${event.id ?? i}-desc`}>
          <ReactMarkdown components={markdownComponents}>{String(formatEventDescription(event, events))}</ReactMarkdown>
        </div>
      ))}
    </div>
  );
};
/* eslint-enable react/prop-types */

export function useStepsList(auditLogEntries: CommerceAuditLogEntry[], initialBalanceCents: number): StepType[] {
  const lastTimestamp = auditLogEntries.at(-1)?.timestamp;
  const creationDateDateTime = lastTimestamp ? DateTime.fromISO(lastTimestamp) : null;
  const isAfterStartDate = creationDateDateTime !== null ? creationDateDateTime >= START_DATE : false;
  const { paymentId } = useParams();

  return useMemo(() => {
    const grouped = groupAuditEventsByTimestamp(auditLogEntries);
    const chronological = [...grouped].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    let runningBalance = initialBalanceCents;

    const withBalance: GroupedAuditEvents[] = chronological.map((group) => {
      const priceCentsChangesets = getPriceCentsChangesets(group.events);
      const delta = calculateBalanceDelta(priceCentsChangesets);

      const oldBalanceCents = runningBalance;
      const newBalanceCents = runningBalance + delta;
      runningBalance = newBalanceCents;

      return {
        ...group,
        balanceChange: priceCentsChangesets.length > 0 ? { oldBalanceCents, newBalanceCents } : undefined
      };
    });

    const newestFirst = withBalance.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return newestFirst.map((group) => {
      const actorName = getActorName(group.events);
      const formattedTime = toDateTimeFormat(group.timestamp, "LLL dd, yyyy h:mm a (ZZZZ)");
      const hasBalanceChange =
        group.balanceChange &&
        !paymentId &&
        isAfterStartDate &&
        !group.events.some((e) => e.description.includes("Order created"));

      return {
        headline: (
          <>
            <Headline events={group.events} />
            <div className="">
              {hasBalanceChange &&
                `Order balance changed from ${centsToDollarsFormatted(
                  group.balanceChange?.oldBalanceCents ?? null
                )} to ${centsToDollarsFormatted(group.balanceChange?.newBalanceCents ?? null)}`}
            </div>
          </>
        ),
        supportingText: `At ${formattedTime}${actorName ? ` by ${actorName}` : ""}`
      };
    });
  }, [auditLogEntries, initialBalanceCents, paymentId, isAfterStartDate]);
}
